import { SegmentedControl } from './DashboardCard';
import type { WorkloadTf } from '../../api/dashboard';

export type WorkloadPreset = '1d' | '5d' | '1mo' | '3mo';
export type WorkloadChartMode = 'conversion' | 'workload';

export const WORKLOAD_PRESET_DAYS: Record<WorkloadPreset, number> = {
  '1d': 1,
  '5d': 5,
  '1mo': 30,
  '3mo': 90,
};

/** Default resolution per preset, as requested: day-1m, 5 days-15m, month-1h. */
export const WORKLOAD_PRESET_TF: Record<WorkloadPreset, WorkloadTf> = {
  '1d': '1m',
  '5d': '15m',
  '1mo': '1h',
  '3mo': '4h',
};

const TF_OPTIONS: Array<{ value: WorkloadTf; label: string }> = [
  { value: '1m', label: '1м' },
  { value: '5m', label: '5м' },
  { value: '15m', label: '15м' },
  { value: '30m', label: '30м' },
  { value: '1h', label: '1ч' },
  { value: '4h', label: '4ч' },
  { value: '1d', label: '1д' },
];

const PRESET_OPTIONS: Array<{ value: WorkloadPreset; label: string }> = [
  { value: '1d', label: '1Д' },
  { value: '5d', label: '5Д' },
  { value: '1mo', label: '1М' },
  { value: '3mo', label: '3М' },
];

export function WorkloadTimeframePicker({
  value,
  onChange,
}: {
  value: WorkloadTf;
  onChange: (value: WorkloadTf) => void;
}) {
  return <SegmentedControl value={value} options={TF_OPTIONS} onChange={onChange} />;
}

export function WorkloadPresetPicker({
  value,
  onChange,
}: {
  value: WorkloadPreset | null;
  onChange: (value: WorkloadPreset) => void;
}) {
  return (
    <div className="inline-flex h-6 items-center rounded-md border border-slate-200 bg-slate-100 p-px">
      {PRESET_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`inline-flex h-5 items-center rounded px-2 text-[10px] font-semibold leading-none transition ${
            value === option.value
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function WorkloadModePicker({
  value,
  onChange,
}: {
  value: WorkloadChartMode;
  onChange: (value: WorkloadChartMode) => void;
}) {
  const conversion = value === 'conversion';
  return (
    <div className="inline-flex items-center gap-2" role="group" aria-label="Режим графика">
      <button
        type="button"
        title="Оплачено / все заявки по реквизиту"
        onClick={() => onChange('conversion')}
        className={`text-base font-bold leading-none transition ${
          conversion ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'
        }`}
      >
        Конверсия
      </button>
      <button
        type="button"
        role="switch"
        aria-checked={!conversion}
        aria-label={conversion ? 'Показать загруженность реквизитов' : 'Показать конверсию'}
        onClick={() => onChange(conversion ? 'workload' : 'conversion')}
        className="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full bg-indigo-500"
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow-md transition-transform ${
            conversion ? 'translate-x-1' : 'translate-x-5'
          }`}
        />
      </button>
      <button
        type="button"
        title="Загруженность реквизитов"
        onClick={() => onChange('workload')}
        className={`text-base font-bold leading-none transition ${
          conversion ? 'text-slate-400 hover:text-slate-600' : 'text-slate-800'
        }`}
      >
        Загруженность реквизитов
      </button>
    </div>
  );
}
